import {
  collection,
  doc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  onSnapshot,
  runTransaction,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from '@/services/firebase/config'
import type { Bid, AnonymizedBid } from '@/types/bid'

const LISTINGS_COLLECTION = 'listings'
const BIDS_COLLECTION = 'bids'

// Calculate minimum bid increment based on current price
export function getMinimumBidIncrement(currentBid: number): number {
  if (currentBid < 10) return 1
  if (currentBid < 50) return 2
  if (currentBid < 100) return 5
  if (currentBid < 500) return 10
  if (currentBid < 1000) return 25
  if (currentBid < 5000) return 50
  if (currentBid < 10000) return 100
  if (currentBid < 50000) return 250
  if (currentBid < 100000) return 500
  return 1000
}

// Get minimum next bid amount
export function getMinimumNextBid(currentBid: number): number {
  return currentBid + getMinimumBidIncrement(currentBid)
}

// Get bid history for a listing
export async function getBidHistory(listingId: string): Promise<Bid[]> {
  const q = query(
    collection(db, LISTINGS_COLLECTION, listingId, BIDS_COLLECTION),
    orderBy('createdAt', 'desc'),
    limit(50)
  )

  const snapshot = await getDocs(q)
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    listingId,
    ...doc.data(),
  })) as Bid[]
}

// Subscribe to bid history (real-time)
export function subscribeToBidHistory(
  listingId: string,
  callback: (bids: Bid[]) => void
): Unsubscribe {
  const q = query(
    collection(db, LISTINGS_COLLECTION, listingId, BIDS_COLLECTION),
    orderBy('createdAt', 'desc'),
    limit(50)
  )

  return onSnapshot(q, (snapshot) => {
    const bids = snapshot.docs.map((doc) => ({
      id: doc.id,
      listingId,
      ...doc.data(),
    })) as Bid[]
    callback(bids)
  })
}

// Anonymize bid history for display
export function anonymizeBidHistory(bids: Bid[], currentUserId?: string): AnonymizedBid[] {
  // Create a map of bidder IDs to display names
  const bidderMap = new Map<string, string>()
  let bidderCount = 0

  // Process bids in chronological order to assign consistent bidder numbers
  const sortedBids = [...bids].sort(
    (a, b) => (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0)
  )

  sortedBids.forEach((bid) => {
    if (!bidderMap.has(bid.bidderId)) {
      bidderCount++
      bidderMap.set(bid.bidderId, `Bidder ${bidderCount}`)
    }
  })

  // Return bids with anonymized display names (but keep original order - newest first)
  return bids.map((bid) => ({
    ...bid,
    displayName:
      bid.bidderId === currentUserId
        ? 'You'
        : bidderMap.get(bid.bidderId) || 'Unknown',
  }))
}

// Place a bid
export async function placeBid(
  listingId: string,
  bidderId: string,
  bidderName: string,
  amount: number
): Promise<{ success: boolean; message: string; newBid?: number }> {
  try {
    const result = await runTransaction(db, async (transaction) => {
      // Get current listing state
      const listingRef = doc(db, LISTINGS_COLLECTION, listingId)
      const listingSnap = await transaction.get(listingRef)

      if (!listingSnap.exists()) {
        throw new Error('Listing not found')
      }

      const listing = listingSnap.data()

      // Check if auction is still active
      if (listing.auctionEnded) {
        throw new Error('This auction has ended')
      }

      if (listing.expiresAt && listing.expiresAt.toMillis() < Date.now()) {
        throw new Error('This auction has expired')
      }

      // Get current bid amount
      const currentBid = listing.currentBid || listing.startingPrice || listing.price || 0
      const minimumBid = getMinimumNextBid(currentBid)

      // Validate bid amount
      if (amount < minimumBid) {
        throw new Error(`Minimum bid is $${minimumBid.toLocaleString()}`)
      }

      // Check if bidding against yourself
      if (listing.highBidderId === bidderId) {
        throw new Error('You are already the highest bidder')
      }

      // Check reserve price
      const reserveMet = !listing.reservePrice || amount >= listing.reservePrice

      // Place the user's own bid. If there's a competing auto-bid from another
      // user (listing.maxBid set, owned by someone else), the counter-response
      // is written by the onBidCreated Cloud Function trigger using the Admin
      // SDK — the client can't write bids on behalf of other users because the
      // Firestore rule requires bidderId === request.auth.uid.
      const bidRef = doc(collection(db, LISTINGS_COLLECTION, listingId, BIDS_COLLECTION))
      transaction.set(bidRef, {
        bidderId,
        bidderName,
        amount,
        isAutoBid: false,
        createdAt: serverTimestamp(),
      })

      transaction.update(listingRef, {
        currentBid: amount,
        bidCount: (listing.bidCount || 0) + 1,
        highBidderId: bidderId,
        highBidderName: bidderName,
        lastBidAt: serverTimestamp(),
        reserveMet,
      })

      return {
        success: true,
        message: reserveMet
          ? 'Bid placed successfully!'
          : 'Bid placed! Reserve not yet met.',
        newBid: amount,
      }
    })

    return result
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to place bid',
    }
  }
}

// Set auto-bid (proxy bid)
export async function setAutoBid(
  listingId: string,
  bidderId: string,
  bidderName: string,
  maxAmount: number
): Promise<{ success: boolean; message: string }> {
  try {
    const result = await runTransaction(db, async (transaction) => {
      const listingRef = doc(db, LISTINGS_COLLECTION, listingId)
      const listingSnap = await transaction.get(listingRef)

      if (!listingSnap.exists()) {
        throw new Error('Listing not found')
      }

      const listing = listingSnap.data()

      // Check if auction is still active
      if (listing.auctionEnded) {
        throw new Error('This auction has ended')
      }

      const currentBid = listing.currentBid || listing.startingPrice || listing.price || 0
      const minimumBid = getMinimumNextBid(currentBid)

      if (maxAmount < minimumBid) {
        throw new Error(`Maximum bid must be at least $${minimumBid.toLocaleString()}`)
      }

      // If there's an existing auto-bid from another user, we need to bid against it
      if (listing.maxBid && listing.maxBidderId !== bidderId) {
        const existingMaxBid = listing.maxBid

        if (maxAmount > existingMaxBid) {
          // New auto-bid is higher - outbid the previous one
          const newBidAmount = getMinimumNextBid(existingMaxBid)

          // Place a bid at the minimum increment above the old max
          const bidRef = doc(collection(db, LISTINGS_COLLECTION, listingId, BIDS_COLLECTION))
          transaction.set(bidRef, {
            bidderId,
            bidderName,
            amount: newBidAmount,
            isAutoBid: true,
            createdAt: serverTimestamp(),
          })

          transaction.update(listingRef, {
            currentBid: newBidAmount,
            bidCount: (listing.bidCount || 0) + 1,
            highBidderId: bidderId,
            highBidderName: bidderName,
            maxBid: maxAmount,
            maxBidderId: bidderId,
            maxBidderName: bidderName,
            lastBidAt: serverTimestamp(),
            reserveMet: !listing.reservePrice || newBidAmount >= listing.reservePrice,
          })

          return {
            success: true,
            message: `Auto-bid set! You're now the highest bidder at $${newBidAmount.toLocaleString()}`,
          }
        } else {
          // New auto-bid is lower than the existing max — place the user's
          // bid at their maxAmount and let the onBidCreated trigger counter
          // from the higher auto-bidder. The lower maxBid isn't stored on
          // the listing because it can't win.
          const bidRef = doc(collection(db, LISTINGS_COLLECTION, listingId, BIDS_COLLECTION))
          transaction.set(bidRef, {
            bidderId,
            bidderName,
            amount: maxAmount,
            isAutoBid: true,
            createdAt: serverTimestamp(),
          })

          transaction.update(listingRef, {
            currentBid: maxAmount,
            bidCount: (listing.bidCount || 0) + 1,
            highBidderId: bidderId,
            highBidderName: bidderName,
            lastBidAt: serverTimestamp(),
            reserveMet: !listing.reservePrice || maxAmount >= listing.reservePrice,
          })

          return {
            success: true,
            message: 'Auto-bid set',
          }
        }
      }

      // No competing auto-bid - just set this one
      const isCurrentHighBidder = listing.highBidderId === bidderId

      if (!isCurrentHighBidder) {
        // Place an initial bid at minimum
        const bidAmount = minimumBid
        const bidRef = doc(collection(db, LISTINGS_COLLECTION, listingId, BIDS_COLLECTION))
        transaction.set(bidRef, {
          bidderId,
          bidderName,
          amount: bidAmount,
          isAutoBid: true,
          createdAt: serverTimestamp(),
        })

        transaction.update(listingRef, {
          currentBid: bidAmount,
          bidCount: (listing.bidCount || 0) + 1,
          highBidderId: bidderId,
          highBidderName: bidderName,
          maxBid: maxAmount,
          maxBidderId: bidderId,
          maxBidderName: bidderName,
          lastBidAt: serverTimestamp(),
          reserveMet: !listing.reservePrice || bidAmount >= listing.reservePrice,
        })

        return {
          success: true,
          message: `Auto-bid set up to $${maxAmount.toLocaleString()}. Current bid: $${bidAmount.toLocaleString()}`,
        }
      }

      // Already highest bidder - just update max bid
      transaction.update(listingRef, {
        maxBid: maxAmount,
        maxBidderId: bidderId,
        maxBidderName: bidderName,
      })

      return {
        success: true,
        message: `Auto-bid updated to $${maxAmount.toLocaleString()}`,
      }
    })

    return result
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to set auto-bid',
    }
  }
}

// Get user's bids on a listing
export async function getUserBidsOnListing(
  listingId: string,
  userId: string
): Promise<Bid[]> {
  const q = query(
    collection(db, LISTINGS_COLLECTION, listingId, BIDS_COLLECTION),
    where('bidderId', '==', userId),
    orderBy('createdAt', 'desc')
  )

  const snapshot = await getDocs(q)
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    listingId,
    ...doc.data(),
  })) as Bid[]
}
